import isRetinaEnvironment from "is-retina";
import { captureException } from "@web-monorepo/telemetry";
import { expectsRetinaSibling, toRetinaPath } from "./retinaPaths.ts";

// A caller passing an already-resolved URL (a data URI, an absolute http(s) URL, or the old
// bundler-emitted "/assets/..." shape) instead of a relative path is a coding mistake at the call
// site, not a missing asset — report it and hand the value back unchanged rather than mangling it
// into a broken URL.
function isAlreadyResolvedImageUrl(path: string): boolean {
  return (
    path.startsWith("data:") || path.startsWith("http:") || path.startsWith("https:") || path.startsWith("/assets/")
  );
}

// Only a path that's expected to have a real @2x object gets rewritten to ask for one — see
// expectsRetinaSibling in retinaPaths.ts, which findMissingRetinaKeys also uses to decide which
// objects must exist. Anything else (an already-@2x/@3x literal, an SVG) is handed back untouched.
function retinaPath(path: string): string {
  return expectsRetinaSibling(path) ? toRetinaPath(path) : path;
}

function reportDoubleResolvedImageUrl(path: string): void {
  captureException(
    new Error(`Image path already resolved: expected a relative path like "assets/images/foo.png", got "${path}"`),
    { extra: { path } },
  );
}

// Builds a getImageSrc for a directory of images uploaded to a CDN at baseUrl. Requests the @2x
// key only on an actual high-DPI screen (same isRetinaEnvironment() check iconSet.ts's getIconSrc
// uses, and what the legacy web/src/components/misc/Image.tsx did before this CDN migration) — a
// standard-density screen gets the plain 1x bytes instead of silently downloading double the
// bytes it can't even render more sharply. There's still no per-call opt-in or generated list:
// the choice is purely "what screen is this," not "does this path have retina" — every base
// *raster* path is required to have a real @2x object regardless (a genuine higher-resolution
// source, or a duplicate of the 1x bytes when no such source exists), since this function doesn't
// know ahead of time which screen it'll be called from. SVGs are exempt in both directions: see
// retinaPath — they're resolution-independent, so there's nothing a 2x copy could add. This
// package doesn't upload images itself (see generateImageThumbhashes.ts) — whoever populates the
// bucket is responsible for that @2x object existing. generateImageThumbhashes.ts refuses to run
// if it finds a base raster path with no @2x sibling, so the gap gets caught at generation time
// rather than discovered as a broken <img> at runtime on a retina screen.
export function createImageSrcBuilder(baseUrl: string) {
  function getImageSrc(path: string): string {
    if (isAlreadyResolvedImageUrl(path)) {
      reportDoubleResolvedImageUrl(path);
      return path;
    }

    return `${baseUrl}/${isRetinaEnvironment() ? retinaPath(path) : path}`;
  }

  return { getImageSrc };
}
